import { defineComponent, renderSlot, watch } from 'vue'
import { buildProps, definePropType } from '@bigin/utils'
import { provideGlobalConfig, useSizeProp } from '@bigin/hooks'

import type { TableConfigContext } from '@bigin/components/table'
import type { ExtractPropTypes } from 'vue'
import type { ExperimentalFeatures } from '@bigin/tokens'
import type { Language } from '@bigin/locale'
import type { ButtonConfigContext } from '@bigin/components/button'
import type { MessageConfigContext } from '@bigin/components/message'

export const messageConfig: MessageConfigContext = {}

export const configProviderProps = buildProps({
  // Controlling if the users want a11y features.
  a11y: {
    type: Boolean,
    default: true,
  },

  locale: {
    type: definePropType<Language>(Object),
  },

  size: useSizeProp,

  button: {
    type: definePropType<ButtonConfigContext>(Object),
  },

  table: {
    type: definePropType<TableConfigContext>(Object),
    default() {
      return {
        sort: {
          ascending: 'ascending',
          descending: 'descending',
        },
      }
    },
  },

  experimentalFeatures: {
    type: definePropType<ExperimentalFeatures>(Object),
  },

  // Controls if we should handle keyboard navigation
  keyboardNavigation: {
    type: Boolean,
    default: true,
  },

  message: {
    type: definePropType<MessageConfigContext>(Object),
  },

  zIndex: Number,

  namespace: {
    type: String,
    default: 'b',
  },
} as const)
export type ConfigProviderProps = ExtractPropTypes<typeof configProviderProps>

const ConfigProvider = defineComponent({
  name: 'BConfigProvider',
  props: configProviderProps,

  setup(props, { slots }) {
    watch(
      () => props.message,
      (val) => {
        Object.assign(messageConfig, val ?? {})
      },
      { immediate: true, deep: true }
    )
    const config = provideGlobalConfig(props)
    return () => renderSlot(slots, 'default', { config: config?.value })
  },
})
export type ConfigProviderInstance = InstanceType<typeof ConfigProvider>

export default ConfigProvider
